/**
 * Реквизиты шапки АОСР проекта (КR9) — на сервере, а не в браузере (П-4: акт
 * собирает код из реквизитов, здесь — форма, перенос и подписи):
 * — форма «Актов ИД» читает и пишет `GET/PATCH /agents/pto/projects/{id}/aosr/requisites`
 *   (`{requisites}` — стороны, объект с адресом, представители по умолчанию;
 *   `PATCH` заменяет пришедшие ключи, неприсланные остаются);
 * — до КR9 веб хранил шапку только в localStorage `pto-act-reqs-{projectId}`:
 *   её предлагаем перенести на сервер, дальше браузер не используем;
 * — мост «заявка Прораба → АОСР» берёт стороны, объект с адресом и
 *   представителей из этих реквизитов; чего в них нет — стороны и объект с
 *   адресом из реквизитов ОЖР (`fallback` ответа), представителей — последних
 *   назначенных по ролям из реестра приказов проекта. Карточка заявки заранее
 *   показывает, что подставится, или предупреждает, что акт выйдет без сторон
 *   и подписантов.
 *
 * Модуль без импортов значений: Node-тесты (`logic.test.mjs`) снимают типы без
 * сборщика и путей-алиасов.
 */
import type {
  PtoAosrRepresentative,
  PtoAosrRequisites,
  PtoAosrRequisitesFallback,
  PtoOrderRepresentative,
} from '@shared/api/types';

/** Поля шапки акта (форма 344/пр), которые ведёт веб. */
export const AOSR_HEADER_KEYS = [
  'form_ref',
  'object_name',
  'object_address',
  'developer',
  'builder',
  'designer',
] as const;

export type AosrHeaderKey = (typeof AOSR_HEADER_KEYS)[number];

/** Шапка акта в форме: строки, не заполнено — ''. */
export type AosrHeaderForm = Record<AosrHeaderKey, string>;

/** Представитель по умолчанию в форме (структура КR9). */
export interface AosrRepForm {
  role: string;
  position: string;
  name: string;
  order_doc: string;
}

/** Реквизиты АОСР проекта в форме: шапка + представители по умолчанию. */
export interface AosrRequisitesForm {
  header: AosrHeaderForm;
  reps: AosrRepForm[];
}

/** Роли представителей формы РД-11-02-2006 — в порядке формы. */
const REP_ROLE_ORDER = [
  'developer_control',
  'builder',
  'builder_control',
  'designer',
  'performer',
  'other',
] as const;

/** Строка JSON → обрезанная строка; иное — ''. */
function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Пустая шапка. */
export function emptyAosrHeader(): AosrHeaderForm {
  return {
    form_ref: '',
    object_name: '',
    object_address: '',
    developer: '',
    builder: '',
    designer: '',
  };
}

/** Представитель заполнен: есть хоть что-то кроме роли. */
export function aosrRepFilled(r: Pick<AosrRepForm, 'position' | 'name' | 'order_doc'>): boolean {
  return !!(r.position.trim() || r.name.trim() || r.order_doc.trim());
}

/**
 * Строка представителя для акта (графа представителя в форме РД-11-02-2006):
 * «должность ФИО, документ о полномочиях» — как писали раньше вручную
 * («Операционный директор Лой А.А., С-74-273928, приказ 215/2-П …»).
 */
export function aosrRepDetails(r: Pick<AosrRepForm, 'position' | 'name' | 'order_doc'>): string {
  const head = [r.position.trim(), r.name.trim()].filter(Boolean).join(' ');
  return [head, r.order_doc.trim()].filter(Boolean).join(', ');
}

/**
 * Представитель с сервера → форма. НРС и организация, если сервер хранит их
 * отдельно, входят в «документ о полномочиях» (в форме это одна строка — так
 * они и печатаются). Запись только с готовой строкой `details` (без должности и
 * документа) — строка целиком уходит в «документ», ФИО для подписи — из
 * `short_name`.
 */
export function aosrRepFromServer(r: PtoAosrRepresentative): AosrRepForm {
  const role = str(r.role) || 'other';
  const position = str(r.position);
  const name = str(r.name) || str(r.short_name);
  const nrs = str(r.nrs);
  const orderDoc = [nrs ? `НРС ${nrs}` : '', str(r.order_doc), str(r.organization)]
    .filter(Boolean)
    .join(', ');
  const details = str(r.details);
  if (!position && !orderDoc && details) {
    return { role, position: '', name, order_doc: details };
  }
  return { role, position, name, order_doc: orderDoc };
}

/** В реквизитах есть хоть одно поле шапки, заполненный представитель или
 *  представитель из реестра приказов. */
export function aosrRequisitesFilled(req: PtoAosrRequisites | null | undefined): boolean {
  if (!req || typeof req !== 'object') return false;
  if (AOSR_HEADER_KEYS.some((k) => str(req[k]))) return true;
  if ((req.representative_ids ?? []).length > 0) return true;
  return (req.representatives ?? []).some((r) => aosrRepFilled(aosrRepFromServer(r)));
}

/** Реквизиты сервера → форма; пусто (null, `{}`, одни пробелы) — null. */
export function aosrFormFromRequisites(
  req: PtoAosrRequisites | null | undefined,
): AosrRequisitesForm | null {
  if (!req || !aosrRequisitesFilled(req)) return null;
  const header = emptyAosrHeader();
  for (const k of AOSR_HEADER_KEYS) header[k] = str(req[k]);
  const reps = (req.representatives ?? []).map(aosrRepFromServer).filter(aosrRepFilled);
  return { header, reps };
}

/**
 * Форма → тело `PATCH {requisites}`: поля шапки строками (пустая — очистить
 * поле) и только заполненные представители — в структуре КR9, с `details` /
 * `short_name` для генерации акта. Чего форма не ведёт (`operator`,
 * `performer`, `representative_ids`), в теле нет — сервер оставляет как было.
 */
export function aosrRequisitesBody(form: AosrRequisitesForm): PtoAosrRequisites {
  const out: PtoAosrRequisites = {};
  for (const k of AOSR_HEADER_KEYS) out[k] = form.header[k].trim();
  out.representatives = form.reps.filter(aosrRepFilled).map((r) => ({
    role: r.role,
    name: r.name.trim(),
    position: r.position.trim(),
    order_doc: r.order_doc.trim(),
    details: aosrRepDetails(r),
    short_name: r.name.trim(),
  }));
  return out;
}

/**
 * Шапка из localStorage `pto-act-reqs-{projectId}` — так веб хранил реквизиты до
 * КR9 (`{formRef, objectName, objectAddress, partyDeveloper, partyBuilder,
 * partyDesigner}`). Битый JSON, не объект, все поля пусты — null (переносить нечего).
 */
export function aosrHeaderFromLocal(raw: string | null | undefined): AosrHeaderForm | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const header: AosrHeaderForm = {
    form_ref: str(o.formRef),
    object_name: str(o.objectName),
    object_address: str(o.objectAddress),
    developer: str(o.partyDeveloper),
    builder: str(o.partyBuilder),
    designer: str(o.partyDesigner),
  };
  return AOSR_HEADER_KEYS.some((k) => header[k]) ? header : null;
}

/** Формы совпадают (нет несохранённых изменений): шапка и заполненные
 *  представители после обрезки пробелов, в том же порядке. */
export function aosrFormsEqual(a: AosrRequisitesForm, b: AosrRequisitesForm): boolean {
  if (AOSR_HEADER_KEYS.some((k) => a.header[k].trim() !== b.header[k].trim())) return false;
  const ra = a.reps.filter(aosrRepFilled);
  const rb = b.reps.filter(aosrRepFilled);
  if (ra.length !== rb.length) return false;
  return ra.every(
    (r, i) =>
      r.role === rb[i].role &&
      r.position.trim() === rb[i].position.trim() &&
      r.name.trim() === rb[i].name.trim() &&
      r.order_doc.trim() === rb[i].order_doc.trim(),
  );
}

/** Короткая подпись роли представителя (подсказка карточки заявки). */
export function aosrRepRoleShort(role: string): string {
  switch (role) {
    case 'developer_control':
      return 'стройконтроль заказчика';
    case 'builder':
      return 'представитель строителя';
    case 'builder_control':
      return 'стройконтроль строителя';
    case 'designer':
      return 'представитель проектировщика';
    case 'performer':
      return 'выполнивший работы';
    default:
      return 'иной представитель';
  }
}

/**
 * Роли, которые мост возьмёт из реестра приказов проекта, если в реквизитах
 * представителей нет: по каждой роли формы — последний назначенный (новый
 * приказ заменяет прежний), в порядке ролей формы.
 */
export function aosrRegistryDefaultRoles(
  registry: readonly Pick<PtoOrderRepresentative, 'role'>[] | null | undefined,
): string[] {
  const roles = new Set((registry ?? []).map((r) => r.role));
  return REP_ROLE_ORDER.filter((role) => roles.has(role));
}

/** Сторона шапки, которую подставит мост; `fromOzhr` — взята из реквизитов ОЖР. */
export interface AosrPreviewParty {
  label: string;
  fromOzhr: boolean;
}

/** Что подставит мост «заявка Прораба → АОСР» в шапку акта (КR9). */
export interface AosrBridgePreview {
  /** ok — есть и стороны, и представители; no_reps — стороны есть, подписантов
   *  нет; no_parties — подписанты есть, сторон нет; empty — ни того ни другого:
   *  акт выйдет без сторон и представителей. */
  level: 'ok' | 'no_reps' | 'no_parties' | 'empty';
  /** «Объект, адрес» из реквизитов (АОСР или ОЖР); null — в акте имя проекта. */
  object: string | null;
  objectFromOzhr: boolean;
  parties: AosrPreviewParty[];
  /** Каких сторон в реквизитах нет. */
  missing: string[];
  /** Подписи ролей представителей, которые подставятся. */
  reps: string[];
  /** Откуда представители: реквизиты проекта, реестр приказов (последние по
   *  ролям) или ниоткуда. */
  repsSource: 'requisites' | 'registry' | null;
  /** Сколько представителей реквизиты берут из реестра приказов (`representative_ids`). */
  repsFromRegistry: number;
}

const PARTY_LABELS: Readonly<Record<'developer' | 'builder' | 'designer', string>> = {
  developer: 'застройщик',
  builder: 'лицо, осуществляющее строительство',
  designer: 'проектировщик',
};

/** Реквизиты ОЖР, которые мост подставит вместо пустых (сервер присылает их в
 *  `fallback`; без него — `metadata.ozhr` проекта). */
export type AosrOzhrFallback = Pick<
  PtoAosrRequisitesFallback,
  'object_name' | 'object_address' | 'developer' | 'builder' | 'designer'
>;

/**
 * Что подставит мост в АОСР по заявке (КR9) — зеркало сборки на сервере:
 * стороны (застройщик или технический заказчик, строитель, проектировщик) и
 * объект с адресом — из реквизитов АОСР проекта, чего там нет — из реквизитов
 * ОЖР; представители — из реквизитов (вручную и из реестра приказов), а если их
 * там нет — последние назначенные по ролям из реестра приказов проекта.
 */
export function aosrBridgePreview(
  aosr: PtoAosrRequisites | null | undefined,
  fallback: AosrOzhrFallback | null | undefined,
  registry?: readonly Pick<PtoOrderRepresentative, 'role'>[] | null,
): AosrBridgePreview {
  const a: PtoAosrRequisites = aosr && typeof aosr === 'object' ? aosr : {};
  const o: AosrOzhrFallback = fallback && typeof fallback === 'object' ? fallback : {};
  const parties: AosrPreviewParty[] = [];
  const missing: string[] = [];
  for (const key of ['developer', 'builder', 'designer'] as const) {
    // Технический заказчик / региональный оператор — та же графа, что у застройщика.
    const own = key === 'developer' ? str(a.developer) || str(a.operator) : str(a[key]);
    if (own) parties.push({ label: PARTY_LABELS[key], fromOzhr: false });
    else if (str(o[key])) parties.push({ label: PARTY_LABELS[key], fromOzhr: true });
    else missing.push(PARTY_LABELS[key]);
  }
  const name = str(a.object_name) || str(o.object_name);
  const address = str(a.object_address) || str(o.object_address);
  const objectFromOzhr =
    (!str(a.object_name) && !!str(o.object_name)) ||
    (!str(a.object_address) && !!str(o.object_address));
  const object = [name, address].filter(Boolean).join(', ') || null;
  const manual = (a.representatives ?? [])
    .map(aosrRepFromServer)
    .filter(aosrRepFilled)
    .map((r) => aosrRepRoleShort(r.role));
  const repsFromRegistry = (a.representative_ids ?? []).length;
  let reps: string[] = [];
  let repsSource: AosrBridgePreview['repsSource'] = null;
  if (manual.length > 0 || repsFromRegistry > 0) {
    reps = manual;
    repsSource = 'requisites';
  } else {
    reps = aosrRegistryDefaultRoles(registry).map(aosrRepRoleShort);
    repsSource = reps.length > 0 ? 'registry' : null;
  }
  const hasReps = reps.length > 0 || repsFromRegistry > 0;
  const level: AosrBridgePreview['level'] = hasReps
    ? parties.length > 0
      ? 'ok'
      : 'no_parties'
    : parties.length > 0
      ? 'no_reps'
      : 'empty';
  return {
    level,
    object,
    objectFromOzhr: !!object && objectFromOzhr,
    parties,
    missing,
    reps,
    repsSource,
    repsFromRegistry,
  };
}

/** Подсказка карточки заявки о шапке будущего АОСР: предупреждение и что подставится. */
export interface AosrBridgePreviewText {
  /** Акт выйдет без сторон и/или подписантов; null — реквизитов хватает. */
  warning: string | null;
  /** «В шапку акта подставятся: объект — …; стороны: …; представители: …». */
  summary: string;
}

/** Подсказка о шапке АОСР по заявке словами ([`aosrBridgePreview`]). */
export function aosrBridgePreviewText(p: AosrBridgePreview): AosrBridgePreviewText {
  const fromOzhr = (label: string, ozhr: boolean) =>
    ozhr ? `${label} (из реквизитов ОЖР)` : label;
  const warning =
    p.level === 'empty'
      ? 'Реквизиты АОСР проекта не заполнены — акт будет без сторон и представителей.'
      : p.level === 'no_reps'
        ? 'В реквизитах АОСР проекта нет представителей (и реестр приказов проекта пуст) — в акте не будет подписантов.'
        : p.level === 'no_parties'
          ? 'В реквизитах АОСР проекта нет сторон (застройщик, строитель, проектировщик) — шапка акта будет без них.'
          : null;
  const parts: string[] = [
    p.object
      ? `объект — ${fromOzhr(p.object, p.objectFromOzhr)}`
      : 'объект — по названию проекта, без адреса',
  ];
  if (p.parties.length > 0) {
    parts.push(`стороны: ${p.parties.map((x) => fromOzhr(x.label, x.fromOzhr)).join(', ')}`);
  }
  if (p.missing.length > 0 && p.parties.length > 0) parts.push(`нет: ${p.missing.join(', ')}`);
  if (p.repsSource === 'registry') {
    parts.push(
      `представители — из реестра приказов проекта (последние назначенные): ${p.reps.join(', ')}`,
    );
  } else if (p.repsSource === 'requisites') {
    const list = [...p.reps];
    if (p.repsFromRegistry > 0) list.push(`из реестра приказов — ${p.repsFromRegistry}`);
    parts.push(`представители: ${list.join(', ')}`);
  }
  return { warning, summary: `В шапку акта подставятся: ${parts.join('; ')}.` };
}

/**
 * Пометки сборки АОСР по заявке о подобранной доказательной базе (ответ
 * `generate-act`, `evidence.evidence_notes`): фото и ТТН подобраны по периоду
 * заявки — сверить перед подписанием. В текст акта они больше не идут (КR9),
 * поэтому их показывает карточка заявки. Нет поля, не массив — пусто.
 */
export function aosrEvidenceNotes(evidence: unknown): string[] {
  if (!evidence || typeof evidence !== 'object') return [];
  const raw = (evidence as { evidence_notes?: unknown }).evidence_notes;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const n of raw) {
    const t = typeof n === 'string' ? n.trim() : '';
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}
